import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { setActiveTab } from "../../SharedModule/redux/reducers/uiShared.reducer";
import {
  HOLIDAYS_ACCESS,
  ROUTE_BAMBOO_TIME_OFF_REPORT,
  ROUTE_CHANGE_TO_HOLIDAYS_REPORT,
  ROUTE_MEAL_BREAK_REPORT,
  ROUTE_TIME_CONFIRMATION_REPORT,
  ROUTE_TIMESHEETS_REPORTS,
  ROUTE_WHO_IS_OUT_REPORT,
  TIME_SUPER_ADMIN_ACCESS,
  TIME_VIEWER_AND_ADMIN_ACCESS,
} from "../../SharedModule/utils/constants";
import DynamicBiIcon from "./../../SharedModule/components/DynamicBiIcon";
import Footer from "../../SharedModule/components/Footer";
import { verifyModuleAccess } from "../../SharedModule/utils/accessUtils";
import { Spinner } from "../../SharedModule/components/Spinner";
import { ForbiddenPage } from "../../SharedModule/pages/ForbiddenPage";
import { Link } from "react-router";
import { BiChevronRight } from "react-icons/bi";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import { TimesheetsService } from "../services/timesheets";
import { generateTitleUSReport } from "../../SharedModule/utils/formatters";
import { selectMetaTimesData } from "../redux/reducers/metaTimes.reducer";
import { useTimesMetadata } from "../hooks/useTimesMetadata";
import { exportToCSV } from "../../SharedModule/utils/exportToExcel";

export const TimesReportsPage = () => {
  useTimesMetadata();
  const dispatch = useDispatch();

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);

  useEffect(() => {
    document.title = "The Hub - Time Reports Page";

    dispatch(setActiveTab(ROUTE_TIMESHEETS_REPORTS));
  }, []);

  const [isLoadingReport, setIsLoadingReport] = useState(false);

  const downloadSalariedReport = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    setIsLoadingReport(true);
    let reportInfo = await TimesheetsService.getTimeEntriesUsSalaried()
      .then((response) => {
        return response;
      })
      .catch(() => {
        dispatch(
          addToast({
            mode: "error",
            message: "Something wrong happened getting US Salaried users",
          })
        );
      });

    let fileName = generateTitleUSReport(
      true,
      reportInfo.payPeriodStartDate,
      reportInfo.payPeriodEndDate
    );
    exportToCSV(reportInfo.employees, fileName, true);
    setIsLoadingReport(false);
  };

  const downloadHourlyReport = async (
    e: React.MouseEvent<HTMLAnchorElement, MouseEvent>
  ) => {
    e.preventDefault();
    setIsLoadingReport(true);
    let reportInfo = await TimesheetsService.getTimeEntriesUsHourly()
      .then((response) => {
        return response;
      })
      .catch(() => {
        dispatch(
          addToast({
            mode: "error",
            message: "Something wrong happened getting US Hourly users",
          })
        );
      });

    let fileName = generateTitleUSReport(
      false,
      reportInfo.payPeriodStartDate,
      reportInfo.payPeriodEndDate
    );
    exportToCSV(reportInfo.employees, fileName, false);
    setIsLoadingReport(false);
  };

  return (
    <>
      {!rolesLoaded && (
        <Spinner
          style={{
            marginLeft: "50%",
            marginTop: "5%",
          }}
        />
      )}
      {rolesLoaded &&
        !verifyModuleAccess(
          true,
          TIME_VIEWER_AND_ADMIN_ACCESS,
          userRolesList
        ) && <ForbiddenPage />}
      {rolesLoaded &&
        verifyModuleAccess(
          true,
          TIME_VIEWER_AND_ADMIN_ACCESS,
          userRolesList
        ) && (
          <div className="content">
            <h2 className="mb-5">Time Reports</h2>

            {isLoadingReport && (
              <Spinner
                style={{
                  marginLeft: "50%",
                  marginTop: "5%",
                }}
              />
            )}

            {!isLoadingReport && (
              <>
                {verifyModuleAccess(
                  true,
                  TIME_VIEWER_AND_ADMIN_ACCESS,
                  userRolesList
                ) && (
                  <div className="d-flex flex-column">
                    <h4 className="mb-0">Overtime / Undertime</h4>
                    <div className="d-flex flex-row flex-wrap">
                      <Link
                        className="card card-width-reports text-decoration-none entry-hour-box-background px-4 pt-3 pb-3 my-3"
                        to={ROUTE_TIME_CONFIRMATION_REPORT}
                      >
                        <div className="d-flex flex-row">
                          <div className="left-column-reports">
                            <div className="d-flex flex-row">
                              <DynamicBiIcon
                                icon="BiBarChartAlt2"
                                className="icon-time-reports ms-1 me-3"
                              />
                              <div className="card-title-time-reports">
                                Time Confirmation
                              </div>
                            </div>
                            <div className="d-flex flex-row mt-3 ms-1">
                              Confirm all the time has been entered in a period,
                              by team or project
                            </div>
                          </div>
                          <div className="right-column-reports">
                            <BiChevronRight className="arrow-icon-nohover" />
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}

                <div className="d-flex flex-column mt-4">
                  <h4 className="mb-0">Time Off</h4>
                  <div className="d-flex flex-row flex-wrap">
                    <Link
                      className="card card-width-reports text-decoration-none entry-hour-box-background px-4 pt-3 pb-3 my-3 me-5"
                      to={ROUTE_WHO_IS_OUT_REPORT}
                    >
                      <div className="d-flex flex-row">
                        <div className="left-column-reports">
                          <div className="d-flex flex-row">
                            <DynamicBiIcon
                              icon="BiBarChartAlt2"
                              className="icon-time-reports ms-1 me-3"
                            />
                            <div className="card-title-time-reports">
                              Who is out in the future
                            </div>
                          </div>
                          <div className="d-flex flex-row mt-3 ms-1">
                            Preview the holidays and time off for a group of
                            users
                          </div>
                        </div>
                        <div className="right-column-reports">
                          <BiChevronRight className="arrow-icon-nohover" />
                        </div>
                      </div>
                    </Link>

                    {verifyModuleAccess(
                      true,
                      HOLIDAYS_ACCESS,
                      userRolesList
                    ) && (
                      <Link
                        className="card card-width-reports text-decoration-none entry-hour-box-background px-4 pt-3 pb-3 my-3 me-5"
                        to={ROUTE_CHANGE_TO_HOLIDAYS_REPORT}
                      >
                        <div className="d-flex flex-row">
                          <div className="left-column-reports">
                            <div className="d-flex flex-row">
                              <DynamicBiIcon
                                icon="BiBarChartAlt2"
                                className="icon-time-reports ms-1 me-3"
                              />
                              <div className="card-title-time-reports">
                                Changes to holidays
                              </div>
                            </div>
                            <div className="d-flex flex-row mt-3 ms-1">
                              Audit Log of changes made by Time Admins to
                              Holidays, by user
                            </div>
                          </div>
                          <div className="right-column-reports">
                            <BiChevronRight className="arrow-icon-nohover" />
                          </div>
                        </div>
                      </Link>
                    )}

                    {verifyModuleAccess(
                      true,
                      TIME_SUPER_ADMIN_ACCESS,
                      userRolesList
                    ) && (
                      <Link
                        className="card card-width-reports text-decoration-none entry-hour-box-background px-4 pt-3 pb-3 my-3"
                        to={ROUTE_BAMBOO_TIME_OFF_REPORT}
                      >
                        <div className="d-flex flex-row">
                          <div className="left-column-reports">
                            <div className="d-flex flex-row">
                              <DynamicBiIcon
                                icon="BiBarChartAlt2"
                                className="icon-time-reports ms-1 me-3"
                              />
                              <div className="card-title-time-reports">
                                BambooHR Export report
                              </div>
                            </div>
                            <div className="d-flex flex-row mt-3 ms-1">
                              Audit log of the daily import of Time Off from
                              BambooHR
                            </div>
                          </div>
                          <div className="right-column-reports">
                            <BiChevronRight className="arrow-icon-nohover" />
                          </div>
                        </div>
                      </Link>
                    )}
                  </div>
                </div>

                {verifyModuleAccess(
                  true,
                  TIME_SUPER_ADMIN_ACCESS,
                  userRolesList
                ) && (
                  <div className="d-flex flex-column mt-4">
                    <h4 className="mb-0">Payroll</h4>
                    <div className="d-flex flex-row flex-wrap">
                      <Link
                        className="card card-width-reports text-decoration-none entry-hour-box-background px-4 pt-3 pb-3 my-3 me-5"
                        to={ROUTE_MEAL_BREAK_REPORT}
                      >
                        <div className="d-flex flex-row">
                          <div className="left-column-reports">
                            <div className="d-flex flex-row">
                              <DynamicBiIcon
                                icon="BiBarChartAlt2"
                                className="icon-time-reports ms-1 me-3"
                              />
                              <div className="card-title-time-reports">
                                Meal Break report
                              </div>
                            </div>
                            <div className="d-flex flex-row mt-3 ms-1">
                              US Hourly employees that are expected to enter a
                              daily Meal Break
                            </div>
                          </div>
                          <div className="right-column-reports">
                            <BiChevronRight className="arrow-icon-nohover" />
                          </div>
                        </div>
                      </Link>

                      <Link
                        className="card card-width-reports text-decoration-none entry-hour-box-background px-4 pt-3 pb-3 my-3 me-5"
                        to={"#"}
                        onClick={(e) => downloadSalariedReport(e)}
                      >
                        <div className="d-flex flex-row">
                          <div className="left-column-reports">
                            <div className="d-flex flex-row">
                              <DynamicBiIcon
                                icon="BiBarChartAlt2"
                                className="icon-time-reports ms-1 me-3"
                              />
                              <div className="card-title-time-reports">
                                US Payroll Export - Salaried
                              </div>
                            </div>
                            <div className="d-flex flex-row mt-3 ms-1">
                              Export US salaried users’ time in the latest pay
                              period for importing into Paychex.
                            </div>
                          </div>
                          <div className="right-column-reports">
                            <BiChevronRight className="arrow-icon-nohover" />
                          </div>
                        </div>
                      </Link>

                      <Link
                        className="card card-width-reports text-decoration-none entry-hour-box-background px-4 pt-3 pb-3 my-3"
                        to={"#"}
                        onClick={(e) => downloadHourlyReport(e)}
                      >
                        <div className="d-flex flex-row">
                          <div className="left-column-reports">
                            <div className="d-flex flex-row">
                              <DynamicBiIcon
                                icon="BiBarChartAlt2"
                                className="icon-time-reports ms-1 me-3"
                              />
                              <div className="card-title-time-reports">
                                US Payroll Export - Hourly
                              </div>
                            </div>
                            <div className="d-flex flex-row mt-3 ms-1">
                              Export US hourly users’ time in the latest pay
                              period for importing into Paychex.
                            </div>
                          </div>
                          <div className="right-column-reports">
                            <BiChevronRight className="arrow-icon-nohover" />
                          </div>
                        </div>
                      </Link>
                    </div>
                  </div>
                )}
              </>
            )}

            <Footer />
          </div>
        )}
    </>
  );
};
